import React, { Fragment, memo } from 'react';
import type { FC } from 'react';
import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import FadeIn from '@atlaskit/motion/fade-in';
import StaggeredEntrance from '@atlaskit/motion/staggered-entrance';

const skeletonWrapperStyle = css({
	marginBottom: token('space.200'),
	// Inlined from `@atlaskit/motion`'s `reduceMotionAsPerUserPreference` (also @deprecated
	// in favour of hardcoding this). Compiled can normally resolve a value imported from
	// another module at build time, but the combination of Atlaspack's zero-install model
	// and Compiled's own resolver means the package isn't in node_modules where that
	// resolver looks (it's a workspace package), so the spread can't be resolved.
	'@media (prefers-reduced-motion: reduce)': {
		animation: 'none',
		transition: 'none',
	},
});

const skeletonBodyStyle = css({
	background: token('color.skeleton'),
	height: '197px',
	width: '100%',
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.large', '8px'),
});

type SkeletonProps = {
	useEntranceAnimation?: boolean;
};

export const FeedCardSkeleton: FC<SkeletonProps> = ({ useEntranceAnimation }) => {
	if (useEntranceAnimation) {
		return (
			<FadeIn entranceDirection="bottom">
				{(animationProps) => (
					<div css={skeletonWrapperStyle} {...animationProps} data-testid="feed-card-skeleton">
						<div data-testid="skeleton-body" css={skeletonBodyStyle} />
					</div>
				)}
			</FadeIn>
		);
	}

	return (
		<div css={skeletonWrapperStyle} data-testid="feed-card-skeleton">
			<div data-testid="skeleton-body" css={skeletonBodyStyle} />
		</div>
	);
};

export const LoadingSkeleton: FC<SkeletonProps> = memo(
	({ useEntranceAnimation }) => {
		const Wrapper = useEntranceAnimation ? StaggeredEntrance : Fragment;
		return (
			<Wrapper columns={1}>
				<FeedCardSkeleton useEntranceAnimation={useEntranceAnimation} />
				<FeedCardSkeleton useEntranceAnimation={useEntranceAnimation} />
			</Wrapper>
		);
	},
	() => true,
);
